import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { financeAccountsQuery, financeCategoriesQuery, newOperationId, plannedOrToday } from '@/lib/finance';
import { fromMinor, toMinor } from '@/lib/money';
import type { FinanceSettleRequest } from '@/api/generated/types.gen';

// OH-205 §3.3: «write off» is no longer a choice of the window — closing at what was paid means the rest is not awaited;
// the server keeps the mode for older clients.
export type SettleRemainder = Exclude<NonNullable<FinanceSettleRequest['remainder']>, 'write_off'>;

export const SETTLE_REMAINDER_OPTIONS: { value: SettleRemainder; label: string }[] = [
  { value: 'restate', label: 'The plan was actually this amount' },
  { value: 'partial', label: 'Part came — keep the rest expected' },
];

/**
 * D-M10b (final review): the withheld-article picker used to match
 * `/комисс|commission|fee/i` against the category's DISPLAY NAME — an
 * unsafe key: the category's `code` is the stable one. An operator renaming "Комиссия (банк)" silently
 * dropped it out of the picker. Matched by `code` instead — the four
 * PSP/bank-side withheld-fee categories the seed (0069_finance_seed)
 * actually ships (bank_fee, vcc_fee, platform_fee, paypal_fee).
 * agent_fee is deliberately excluded: a capital-type agent commission, not
 * a settlement-time withheld PSP/bank fee.
 */
const WITHHELD_FEE_CATEGORY_CODES = new Set(['bank_fee', 'vcc_fee', 'platform_fee', 'paypal_fee']);

/**
 * Ruling FR-24 (review S3-R04): one operation_id per logical operation,
 * where the operation is the BODY. An unchanged retry (a lost response, a
 * transient error) reuses the id so the server replays instead of settling
 * twice; any change to what is sent — e.g. a remainder mode picked after a
 * 422 «remainder is required» — is a different operation and gets a fresh
 * id. `fingerprint` is the last body handed in, minus the id.
 *
 * Returns the function the form calls with each body's content at submit
 * time (useSettleForm.buildBody, and ConfirmReceiptDialog, which builds its
 * body from the settle draft — OH-193 [PR-07]).
 */
export function useSettleOperationId(): (content: object) => string {
  const op = useRef<{ fingerprint: string | null; id: string }>({ fingerprint: null, id: newOperationId() });
  return (content) => {
    const fingerprint = JSON.stringify(content);
    if (op.current.fingerprint !== null && op.current.fingerprint !== fingerprint) op.current.id = newOperationId();
    op.current.fingerprint = fingerprint;
    return op.current.id;
  };
}

export interface UseSettleFormOptions {
  /** A PSP-fact projection (capture/refund) settles at its OWN fixed amount — no remainder question, only a withheld line for the shortfall. A plain row settles like an expected target. */
  isProjection: boolean;
  /** The target's own plan amount, as a positive magnitude. */
  planMinor: number;
  /** The target's own currency (the fallback before an account is picked). */
  currency: string;
  businessTz?: string;
  /** When the payment was planned (a date or a timestamp): the default date once it has come (OH-201). */
  plannedOn?: string;
}

/**
 * Fix round 1, item 4 (review Important — verbatim duplication): the
 * settle-form logic block shared by ConfirmReceiptDialog.tsx (a booking's
 * schedule row) and SettleProviderFactForm (RefundActionDialogs.tsx, a
 * provider fact directly) — same equation (§8.2/§4: bank = settle + income +
 * expense − withheld), same account/currency state, same async-loaded-
 * accounts default-selection fix, same validation sequence. Extracted here
 * so the two forms cannot drift (e.g. a future fix to the account-default
 * bug landing in only one copy, as flagged by the review).
 *
 * Returns state + setters + derived values; each caller still builds its own
 * mutate() call (they use different mutation hooks with different variable
 * shapes) via `buildBody()`, which validates and either returns a ready
 * FinanceSettleRequest or sets `error` and returns null.
 */
export function useSettleForm({ isProjection, planMinor, currency: targetCurrency, businessTz, plannedOn }: UseSettleFormOptions) {
  const { data: accounts = [] } = useQuery(financeAccountsQuery());
  const { data: categories = [] } = useQuery(financeCategoriesQuery());
  const activeCategories = categories.filter((c) => c.is_active);
  const feeCategories = activeCategories.filter((c) => WITHHELD_FEE_CATEGORY_CODES.has(c.code));

  const preferredAccount = accounts.find((a) => a.currencies.includes(targetCurrency)) ?? accounts[0];
  const operationIdFor = useSettleOperationId();
  const [accountId, setAccountId] = useState('');
  const account = accounts.find((a) => a.id === accountId);
  const [currency, setCurrency] = useState(targetCurrency);
  // accounts loads asynchronously (this form fetches it itself) — a plain
  // useState initializer only ever sees the FIRST render's empty array, so
  // the default account is applied here, once, the first time the query
  // actually has data and nothing has been picked yet (the bug the review
  // flagged as fixed in one copy but at risk of drifting in the other).
  useEffect(() => {
    if (accountId || !preferredAccount) return;
    setAccountId(preferredAccount.id);
    setCurrency(preferredAccount.currencies.includes(targetCurrency) ? targetCurrency : (preferredAccount.currencies[0] ?? targetCurrency));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredAccount]);
  const [occurredOn, setOccurredOn] = useState(() => plannedOrToday(plannedOn, businessTz));
  const [bankMajor, setBankMajor] = useState(() => fromMinor(planMinor, targetCurrency));
  const [withheldCategoryId, setWithheldCategoryId] = useState('');
  // Ruling FR-28: the withheld amount the operator types, in the ACCOUNT
  // currency — used only for a projection paid in another currency.
  const [withheldMajor, setWithheldMajor] = useState('');
  // OH-205 §3.3: the row closes at what was paid unless the operator says «part came» — the same in every currency.
  const [remainder, setRemainder] = useState<SettleRemainder>('restate');
  const [receipts, setReceipts] = useState<{ id: string; name: string }[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sameCurrency = currency === targetCurrency;
  const bankMinor = toMinor(bankMajor, currency);
  // A projection's withheld part, in the payment (account) currency.
  // Same currency — derived: plan − bank. Another currency (Ruling FR-28) —
  // ENTERED by the operator, in the account currency: the bank amount
  // cannot be compared to the fact's own currency without the NBS rate,
  // which only the server knows for occurred_on. The server derives the
  // settle line from bank + withheld, converts it to the fact currency and
  // refuses a mismatch beyond one minor unit (naming the withheld that
  // balances). Empty input = no withheld line; unparseable = null.
  const withheldEntered = isProjection && !sameCurrency;
  const withheldMinor = useMemo(() => {
    if (!isProjection) return null;
    if (withheldEntered) return withheldMajor.trim() === '' ? 0 : toMinor(withheldMajor, currency);
    return bankMinor !== null ? planMinor - bankMinor : null;
  }, [isProjection, withheldEntered, withheldMajor, currency, bankMinor, planMinor]);

  function buildBody(): FinanceSettleRequest | null {
    if (!accountId) {
      setError('Choose the account the money landed on.');
      return null;
    }
    if (bankMinor === null || bankMinor <= 0) {
      setError(`Enter the amount that hit the account, in ${currency}.`);
      return null;
    }
    if (withheldEntered && (withheldMinor === null || withheldMinor < 0)) {
      setError(`Enter the withheld amount in ${currency}, or leave it empty.`);
      return null;
    }
    if (isProjection && withheldMinor !== null && withheldMinor < 0) {
      setError('Bank amount cannot exceed the fact amount for a provider fact.');
      return null;
    }
    if (isProjection && withheldMinor !== null && withheldMinor > 0 && !withheldCategoryId) {
      setError('Pick the fee article for the withheld part.');
      return null;
    }
    setError(null);
    const content: Omit<FinanceSettleRequest, 'operation_id'> = {
      account_id: accountId,
      currency,
      amount_minor: bankMinor,
      occurred_on: occurredOn,
      lines:
        isProjection && withheldMinor && withheldMinor > 0
          ? [{ kind: 'withheld', category_id: withheldCategoryId, amount_minor: withheldMinor }]
          : [],
      attachment_ids: receipts.map((r) => r.id),
    };
    return { operation_id: operationIdFor(content), ...content };
  }

  return {
    accounts,
    // every article, active or not — for LABELS (a suggested line may carry an
    // article deactivated since); the pickers use activeCategories / feeCategories
    categories,
    activeCategories,
    feeCategories,
    accountId,
    setAccountId,
    account,
    currency,
    setCurrency,
    occurredOn,
    setOccurredOn,
    bankMajor,
    setBankMajor,
    withheldCategoryId,
    setWithheldCategoryId,
    withheldEntered,
    withheldMajor,
    setWithheldMajor,
    remainder,
    setRemainder,
    receipts,
    setReceipts,
    uploadError,
    setUploadError,
    error,
    setError,
    withheldMinor,
    targetCurrency,
    buildBody,
  };
}
